import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, Loader2, ChevronDown } from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';
import { Textarea } from '../components/ui/textarea';
import {
  getSettings,
  getEntry,
  deleteEntry,
  saveEntry,
  getDraft,
  saveDraft,
  clearDraft,
  formatDate,
  formatDisplayDate,
  getEnabledGoals,
  getEnabledPrompts,
  getPlaceholder,
} from '../utils/storage';

const getLast7Days = () => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = formatDate(date);
    days.push({
      value: dateStr,
      label: i === 0 ? 'Today' : i === 1 ? 'Yesterday' : formatDisplayDate(dateStr),
    });
  }
  return days;
};

// Capitalize first letter
const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

export const DailyLog = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editDate = searchParams.get('date');

  const today = formatDate(new Date());
  const [selectedDate, setSelectedDate] = useState(editDate || today);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateOptions = getLast7Days();

  const settings = getSettings();
  const userName = settings?.name || 'friend';
  const goals = getEnabledGoals();
  const prompts = getEnabledPrompts();
  const isEditing = !!getEntry(selectedDate);

  // State for effective goals/prompts for this entry (may be historical)
  const [effectiveGoals, setEffectiveGoals] = useState(goals);
  const [effectivePrompts, setEffectivePrompts] = useState(prompts);

   // Build initial form state using current goals/prompts
  const buildEmptyForm = () => ({
    goals: goals.reduce((acc, g) => {
      acc[g.id] = { completed: false, easyActivity: false };
      return acc;
    }, {}),
    answers: prompts.reduce((acc, p) => {
      acc[p.answerKey] = '';
      return acc;
    }, {}),
  });

  const [formData, setFormData] = useState(buildEmptyForm);
  const [showMessage, setShowMessage] = useState(false);
  const [completionMessage, setCompletionMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [entry, setEntry] = useState(null);
  const [editableComments, setEditableComments] = useState([]);
  const formDataRef = useRef(formData);
  const selectedDateRef = useRef(selectedDate);
  const isLoadedRef = useRef(isLoaded);

  const buildFallbackGoalsFromEntry = (existingEntry) => {
    const entryGoalIds = Object.keys(existingEntry?.goals || {});
    if (entryGoalIds.length === 0) return goals;

    const goalById = new Map(goals.map(g => [g.id, g]));
    return entryGoalIds.map((goalId, index) => {
      const currentGoal = goalById.get(goalId);
      return currentGoal || {
        id: goalId,
        text: `Goal ${index + 1}`,
        easyText: '',
        order: index,
      };
    });
  };

  const buildFallbackPromptsFromEntry = (existingEntry) => {
    const answerKeys = Object.keys(existingEntry || {})
      .filter((key) => /^answer\d+$/.test(key));

    if (answerKeys.length === 0) return prompts;

    const promptByAnswerKey = new Map(prompts.map(p => [p.answerKey, p]));
    return answerKeys
      .sort((a, b) => Number(a.replace('answer', '')) - Number(b.replace('answer', '')))
      .map((answerKey, index) => {
        const currentPrompt = promptByAnswerKey.get(answerKey);
        return currentPrompt || {
          id: `legacy_${answerKey}`,
          question: `Reflection ${index + 1}`,
          answerKey,
          order: index,
        };
      });
  };

  const snapshotFromEffective = (goalDefs, promptDefs) => ({
    goalSnapshots: (goalDefs || []).map((g, index) => ({
      id: g.id,
      text: g.text,
      easyText: g.easyText || '',
      order: g.order ?? index,
    })),
    promptSnapshots: (promptDefs || []).map((p, index) => ({
      id: p.id,
      question: p.question,
      answerKey: p.answerKey,
      order: p.order ?? index,
    })),
  });

  // Load existing entry or draft when date changes - WITH SNAPSHOT SUPPORT
  useEffect(() => {
    const existingEntry = getEntry(selectedDate);
    const draft = getDraft(selectedDate);
    const isTodayEntry = selectedDate === today;

    if (existingEntry) {
      setEntry(existingEntry);
      setEditableComments((existingEntry.comments || []).map(comment => ({ ...comment })));
      if (isTodayEntry) {
        // Today's entry always follows current settings so new goals/prompts appear immediately.
        setEffectiveGoals(goals);
        setEffectivePrompts(prompts);

        const goalData = {};
        goals.forEach(g => {
          goalData[g.id] = existingEntry.goals?.[g.id] || { completed: false, easyActivity: false };
        });

        const answerData = {};
        prompts.forEach(p => {
          answerData[p.answerKey] = existingEntry[p.answerKey] || '';
        });

        if (draft) {
          goals.forEach(g => {
            goalData[g.id] = draft.goals?.[g.id] || goalData[g.id];
          });
          prompts.forEach(p => {
            const draftValue = draft[p.answerKey] ?? draft.answers?.[p.answerKey];
            if (draftValue !== undefined) {
              answerData[p.answerKey] = draftValue;
            }
          });
        }

        setFormData({ goals: goalData, answers: answerData });
        setIsLoaded(true);
        return;
      }

      // Check if this entry has snapshots (historical) or is using current data
      const hasGoalSnapshots = Array.isArray(existingEntry.goalSnapshots) && existingEntry.goalSnapshots.length > 0;
      const hasPromptSnapshots = Array.isArray(existingEntry.promptSnapshots) && existingEntry.promptSnapshots.length > 0;

      const resolvedGoals = hasGoalSnapshots ? existingEntry.goalSnapshots : buildFallbackGoalsFromEntry(existingEntry);
      const resolvedPrompts = hasPromptSnapshots ? existingEntry.promptSnapshots : buildFallbackPromptsFromEntry(existingEntry);

      setEffectiveGoals(resolvedGoals);
      setEffectivePrompts(resolvedPrompts);

      const goalData = {};
      resolvedGoals.forEach(goalDef => {
        goalData[goalDef.id] = existingEntry.goals?.[goalDef.id] || { completed: false, easyActivity: false };
      });

      const answerData = {};
      resolvedPrompts.forEach(promptDef => {
        answerData[promptDef.answerKey] = existingEntry[promptDef.answerKey] || '';
      });

      // If a draft exists for this existing entry, prefer draft values.
      if (draft) {
        resolvedGoals.forEach(goalDef => {
          goalData[goalDef.id] = draft.goals?.[goalDef.id] || goalData[goalDef.id];
        });
        resolvedPrompts.forEach(promptDef => {
          const draftValue = draft[promptDef.answerKey] ?? draft.answers?.[promptDef.answerKey];
          if (draftValue !== undefined) {
            answerData[promptDef.answerKey] = draftValue;
          }
        });
      }

      setFormData({ goals: goalData, answers: answerData });
    } else if (draft) {
      setEntry(null);
      setEditableComments([]);
      // For drafts, use current goals and prompts (drafts are temporary)
      setEffectiveGoals(goals);
      setEffectivePrompts(prompts);
      
      const goalData = {};
      goals.forEach(g => {
        goalData[g.id] = draft.goals?.[g.id] || { completed: false, easyActivity: false };
      });
      const answerData = {};
      prompts.forEach(p => {
        answerData[p.answerKey] = draft[p.answerKey] || draft.answers?.[p.answerKey] || '';
      });
      setFormData({ goals: goalData, answers: answerData });
    } else {
      setEntry(null);
      setEditableComments([]);
      // New entry - use current goals/prompts
      setEffectiveGoals(goals);
      setEffectivePrompts(prompts);
      
      setFormData(buildEmptyForm());
    }
    setIsLoaded(true);
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep refs in sync so lock/background handlers always have current values.
  useEffect(() => {
    formDataRef.current = formData;
    selectedDateRef.current = selectedDate;
    isLoadedRef.current = isLoaded;
  }, [formData, selectedDate, isLoaded]);

  // Autosave draft immediately for both new and existing entries.
  useEffect(() => {
    if (!isLoaded) return;

    saveDraft(selectedDate, {
      goals: formData.goals,
      ...formData.answers,
    });
  }, [formData, selectedDate, isLoaded]);

  // Auto-save draft when app goes to background or screen locks.
  // Keep listeners stable to avoid missing lock events between re-subscribes.
  useEffect(() => {
    const handleBackgroundSave = () => {
      if (!isLoadedRef.current) return;
      const currentDate = selectedDateRef.current;
      // Silent save as draft (no AI, no message)
      const currentFormData = formDataRef.current;
      saveDraft(currentDate, {
        goals: currentFormData.goals,
        ...currentFormData.answers,
      });
    };

    // Listen for page visibility change (tab switch, lock screen)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleBackgroundSave();
      }
    };

    // Capacitor app state change (iOS/Android background)
    let appStateListener;
    let isDisposed = false;
    const setupAppListener = async () => {
      try {
        const { App: CapacitorApp } = await import('@capacitor/app');
        const listener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            handleBackgroundSave();
          }
        });
        if (isDisposed) {
          listener.remove();
          return;
        }
        appStateListener = listener;
      } catch (err) {
        // Not in Capacitor environment (web preview)
        console.log('Capacitor App listener not available');
      }
    };
    setupAppListener();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBackgroundSave);

    return () => {
      isDisposed = true;
      handleBackgroundSave();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBackgroundSave);
      if (appStateListener) {
        appStateListener.remove();
      }
    };
  }, []);

  const handleGoalToggle = (goalId, type) => {
    setFormData(prev => {
      const nextFormData = {
        ...prev,
        goals: {
          ...prev.goals,
          [goalId]: {
            ...prev.goals[goalId],
            [type]: !prev.goals[goalId]?.[type],
          },
        },
      };

      // Persist immediately so a quick app lock cannot lose this update.
      saveDraft(selectedDate, {
        goals: nextFormData.goals,
        ...nextFormData.answers,
      });

      return nextFormData;
    });
  };

  const handleAnswerChange = (answerKey, value) => {
    setFormData(prev => {
      const nextFormData = {
        ...prev,
        answers: { ...prev.answers, [answerKey]: value },
      };

      // Persist immediately so a quick app lock cannot lose this update.
      saveDraft(selectedDate, {
        goals: nextFormData.goals,
        ...nextFormData.answers,
      });

      return nextFormData;
    });
  };

  const handleCommentChange = (index, value) => {
    setEditableComments(prev => prev.map((comment, i) => (
      i === index ? { ...comment, text: value } : comment
    )));
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const isToday = selectedDate === today;
      const existingEntry = getEntry(selectedDate);
      const isNewEntry = !existingEntry;

      const entryData = {
        goals: formData.goals,
        migrated: true,
        savedAt: new Date().toISOString(),
      };

      if (isEditing || isToday) {
        Object.assign(entryData, snapshotFromEffective(effectiveGoals, effectivePrompts));
      }

      // Initialize comments array if this is a new entry
      if (isNewEntry) {
        entryData.comments = [];
      } else {
        entryData.comments = editableComments
          .map(comment => ({
            ...comment,
            text: (comment.text || '').trim(),
          }))
          .filter(comment => comment.text)
          .map(comment => ({
            ...comment,
            date: comment.date || formatDate(new Date()),
            timestamp: comment.timestamp || new Date().toISOString(),
          }));
      }

      // Store each answer as a top-level field
      effectivePrompts.forEach(p => {
        entryData[p.answerKey] = (formData.answers[p.answerKey] || '').trim() || '–';
      });

      saveEntry(selectedDate, entryData);
      clearDraft(selectedDate);
      navigate('/history');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    if (!isEditing) return;

    const confirmed = window.confirm('Are you sure you want to delete this entry?');
    if (!confirmed) return;

    deleteEntry(selectedDate);
    clearDraft(selectedDate);
    navigate('/history');
  };

  const GoalCheckbox = ({ checked, onToggle, label, testId }) => (
    <button
      onClick={onToggle}
      className={`flex items-center gap-3 w-full p-3 rounded-xl transition-all ${
        checked
          ? 'bg-wisteria-100 border border-theme-primary'
          : 'bg-wisteria-50/50 border border-theme-secondary hover:border-theme-primary'
      }`}
      data-testid={testId}
      aria-pressed={checked}
    >
      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
        checked ? 'bg-wisteria-200 border-theme-primary' : 'border-theme-primary bg-theme-light'
      }`}>
        {checked && <Check size={14} strokeWidth={2.5} className="text-wisteria-700" />}
      </div>
      <span className={`font-body text-sm ${checked ? 'text-primary' : 'text-secondary'}`}>{label}</span>
    </button>
  );

  // Determine which goals and prompts to display based on whether entry has snapshots
  const displayGoals = effectiveGoals;
  const displayPrompts = effectivePrompts;

  return (
    <PageWrapper className="bg-app">
      <AnimatePresence>
        {showMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-theme-light backdrop-blur-sm"
            data-testid="completion-message"
          >
            <motion.p
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center text-primary font-heading text-2xl font-light px-8 max-w-sm"
            >
              {completionMessage}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-md mx-auto px-6 py-6">
        {/* Header with date picker */}
        <div className="relative flex items-center gap-4 mb-8">
          <button
  onClick={() => {
    // Silent save - no AI, no message
    const entryData = {
      goals: formData.goals,
      migrated: true,
      savedAt: new Date().toISOString(),
    };
    if (isEditing || selectedDate === today) {
      Object.assign(entryData, snapshotFromEffective(effectiveGoals, effectivePrompts));
    }
    if (isEditing) {
      entryData.comments = editableComments
        .map(comment => ({
          ...comment,
          text: (comment.text || '').trim(),
        }))
        .filter(comment => comment.text)
        .map(comment => ({
          ...comment,
          date: comment.date || formatDate(new Date()),
          timestamp: comment.timestamp || new Date().toISOString(),
        }));
    }
    effectivePrompts.forEach(p => {
      entryData[p.answerKey] = (formData.answers[p.answerKey] || '').trim() || '–';
    });
    saveEntry(selectedDate, entryData);
    clearDraft(selectedDate);
    navigate(isEditing ? '/history' : '/');
  }}
  className="p-2 rounded-full hover:bg-wisteria-50 transition-colors"
  data-testid="back-btn"
  aria-label="Go back"
>
  <ArrowLeft size={24} strokeWidth={1.5} className="text-secondary" />
</button>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 font-heading text-2xl font-light text-primary active:opacity-70 transition-opacity"
              data-testid="date-picker-btn"
            >
              {dateOptions.find(d => d.value === selectedDate)?.label || formatDisplayDate(selectedDate)}
              <ChevronDown size={20} className={`transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
            </button>

            {showDatePicker && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowDatePicker(false)}
                />
                <div
                  className="absolute top-full left-0 mt-2 bg-theme-light rounded-2xl shadow-xl border border-theme-secondary overflow-hidden z-50 min-w-[220px]"
                  style={{ maxHeight: '300px', overflowY: 'auto' }}
                  data-testid="date-dropdown"
                >
                  {dateOptions.map((option) => {
                    const hasEntry = !!getEntry(option.value);
                    return (
                      <button
                        key={option.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDate(option.value);
                          setShowDatePicker(false);
                          setIsLoaded(false);
                        }}
                        className={`w-full px-4 py-3.5 text-left font-body transition-colors flex items-center justify-between ${
                          selectedDate === option.value
                            ? 'bg-wisteria-100 text-wisteria-800'
                            : 'active:bg-wisteria-50 text-primary'
                        }`}
                        data-testid={`date-option-${option.value}`}
                      >
                        <span>{option.label}</span>
                        {hasEntry && (
                          <span className="text-xs bg-wisteria-200 text-wisteria-700 px-2 py-0.5 rounded-full">
                            Saved
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

        {/* Goals - using displayGoals (snapshots for old entries) */}
        {displayGoals.length > 0 && (
          <div className="space-y-3 mb-8">
            {displayGoals.map((goal, index) => {
              const gData = formData.goals[goal.id] || { completed: false, easyActivity: false };
              return (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="space-y-2"
                  data-testid={`goal-row-${goal.id}`}
                >
                  <GoalCheckbox
                    checked={gData.completed}
                    onToggle={() => handleGoalToggle(goal.id, 'completed')}
                    label={cap(goal.text)}
                    testId={`goal-${goal.id}-yes`}
                  />
                  {goal.easyText && (
                    <GoalCheckbox
                      checked={gData.easyActivity}
                      onToggle={() => handleGoalToggle(goal.id, 'easyActivity')}
                      label={cap(goal.easyText)}
                      testId={`goal-${goal.id}-easy`}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Prompts / Reflections - using displayPrompts (snapshots for old entries) */}
        <div className="space-y-6 mb-8">
          {displayPrompts.map((prompt, index) => {
            const isMultiline = prompt.question.toLowerCase() === 'daily notes' || prompt.question.length > 60;
            return (
              <motion.div
                key={prompt.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.05 }}
              >
                <label className="block text-secondary font-body text-sm mb-2">
                  {prompt.question}:
                </label>
                <Textarea
                  value={formData.answers[prompt.answerKey] || ''}
                  onChange={(e) => handleAnswerChange(prompt.answerKey, e.target.value)}
                  className={`bg-theme-light border-theme-secondary rounded-2xl p-4 focus:ring-theme-primary focus:bg-theme-light font-body ${
                    isMultiline ? 'min-h-[120px]' : 'min-h-[100px]'
                  }`}
                  placeholder={getPlaceholder(prompt)}
                  data-testid={`input-${prompt.answerKey}`}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Comments Section (editable when editing) */}
        {isEditing && editableComments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="mb-8"
          >
            <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-4">
              Comments
            </h2>
            <div className="space-y-3">
              {editableComments.map((comment, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.58 + index * 0.05 }}
                  className="p-4 rounded-2xl bg-theme-light border border-theme-secondary"
                >
                  <Textarea
                    value={comment.text || ''}
                    onChange={(e) => handleCommentChange(index, e.target.value)}
                    className="bg-theme-light border-theme-secondary rounded-xl p-3 focus:ring-theme-primary focus:bg-theme-light font-body min-h-[90px] mb-2"
                    data-testid={`edit-comment-${index}`}
                  />
                  <p className="font-body text-xs text-secondary/60">
                    {formatDisplayDate(comment.date)}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col items-center gap-4 pb-8"
        >
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="w-48 bg-wisteria-200 text-wisteria-900 rounded-full px-8 py-4 font-body font-medium transition-all hover:bg-wisteria-300 hover:-translate-y-0.5 shadow-sm disabled:opacity-50"
            data-testid="save-btn"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>

          {isEditing && (
            <button
              onClick={handleDelete}
              className="w-48 rounded-full border border-theme-secondary bg-theme-light px-8 py-4 font-body font-medium text-secondary transition-all hover:bg-wisteria-50 hover:-translate-y-0.5 shadow-sm"
              data-testid="delete-btn"
            >
              Delete entry
            </button>
          )}
        </motion.div>
      </div>
    </PageWrapper>
  );
};

export default DailyLog;